import { Clip } from "../studio/models/Timeline";

/**
 * クリップリストを渡して、タイムライン全体の合計再生時間を取得（ミリ秒）
 * @param clipList クリップリストを渡す
 * @returns タイムライン全体の合計再生時間
 */
export const GetTotalTimeFromClipList = (clipList: Clip[]) => {
    let timelineTotalTime = 0;
    clipList.forEach((clip) => {
        const clipTime = GetClipTimeFromClip(clip);
        timelineTotalTime += clipTime;
    });
    return timelineTotalTime;
};

/**
 * clipTimesを渡して、タイムライン全体の合計再生時間を取得（ミリ秒）
 * @param clipTimes クリップリストを渡す
 * @returns タイムライン全体の合計再生時間
 */
export const GetTotalTimeFromClipTimes = (clipTimes: number[]) => {
    let timelineTotalTime = 0;
    clipTimes.forEach((clipTime) => {
        timelineTotalTime += clipTime;
    });
    return timelineTotalTime;
};

/**
 * クリップの再生時間（ミリ秒）を取得する
 * @param clip クリップ情報を渡す
 * @returns クリップの再生時間を返す（ミリ秒）
 */
export const GetClipTimeFromClip = (clip: Clip): number => {
    if (typeof clip.start !== "number" || typeof clip.end !== "number") return 0;
    return clip.end - clip.start;
};

/**
 * クリップのそれぞれの再生時間（ミリ秒）を格納する配列を取得する
 * @param clipList クリップリスト情報を渡す
 * @returns クリップのそれぞれの再生時間（ミリ秒）を格納する配列
 */
export const GetClipTimesFromCliplist = (clipList: Clip[]): number[] => {
    const timelineClipTimes: number[] = [];
    if (!clipList) return timelineClipTimes;

    clipList.forEach((clip) => {
        const clipTime = GetClipTimeFromClip(clip);
        timelineClipTimes.push(clipTime);
    });
    return timelineClipTimes;
};

/**
 * 再生中のクリップまでclipTimesの合計時間を取得
 * @param sourceIndex 再生中クリップのINDEX
 * @param clipTimes クリップのそれぞれの再生時間（ミリ秒）を格納する配列
 * @returns 再生中のクリップまでclipTimesの合計時間
 */
export const GetClipEndTime = (sourceIndex: number, clipTimes: number[]): number => {
    if (!clipTimes) return 0;

    let clipTimesCalc = 0;
    clipTimes.forEach((time, index) => {
        if (sourceIndex >= index) {
            clipTimesCalc += time;
        }
    });
    return clipTimesCalc;
};

/**
 * 指定位置の再生が出来るフッテージURLを取得
 * @param clip クリップ情報を渡す
 * @returns 指定位置の再生が出来るフッテージURL
 */
export const GetFootageUrlFromClip = (clip: Clip) => {
    let sourceUrl = clip.sourceUrl;
    const token = localStorage.getItem("cineonhub_access_token");
    if (!clip) return sourceUrl;
    sourceUrl =
        process.env.REACT_APP_API_ENDPOINT +
        "/api/footageproxy/" +
        clip.footageId +
        "?Authorization=" +
        token;
    return sourceUrl;
};

/**
 * 指定位置の再生が出来るフッテージURLを取得
 * @param id フッテージIDを渡す
 * @returns 指定位置の再生が出来るフッテージURL
 */
export const GetFootageUrlFromId = (id: number) => {
    const token = localStorage.getItem("cineonhub_access_token");
    const sourceUrl =
        process.env.REACT_APP_API_ENDPOINT + "/api/footageproxy/" + id + "?Authorization=" + token;
    return sourceUrl;
};

/**
 * 指定位置の再生が出来るタイライムURLを取得
 * @param id タイライムIDを渡す
 * @returns 指定位置の再生が出来るタイライムURL
 */
export const GetTimelineUrlFromId = (id: number) => {
    const sourceUrl = process.env.REACT_APP_API_ENDPOINT + "/api/timeline/" + id;
    return sourceUrl;
};

/**
 * クリップ情報内からビデオの指定再生位置を取得
 * @param clip クリップ情報を渡す
 * @returns ビデオの指定再生位置
 */
export const GetSetTimeFromClip = (clip: Clip) => {
    let setTime = "";
    if (!clip || clip.end === 0) return setTime;
    setTime = `#t=${clip.start / 1000},${clip.end / 1000}`;
    return setTime;
};

/**
 * タイムライン指定位置のクリップindexを取得
 * @param currentTimeProp タイムラインの現在再生時間（ミリ秒）
 * @param clipTimes クリップのそれぞれの再生時間（ミリ秒）を格納する配列
 * @returns クリップindex
 */
export const GetCurrentClipIndexFromTime = (
    currentTimeProp: number,
    clipTimes: number[],
    sourceLength?: number
): number => {
    if (!clipTimes || clipTimes.length === 0 || !currentTimeProp) return 0;

    let isOnce = true;
    let currentClipIndex = 0;
    let timelineCurrentTimeDecrease = currentTimeProp;
    const totalTime = GetTotalTimeFromClipTimes(clipTimes);
    clipTimes.forEach((clipTime, index) => {
        timelineCurrentTimeDecrease -= clipTime;

        if (timelineCurrentTimeDecrease < 0 && isOnce) {
            currentClipIndex = index;
            isOnce = false;
        }
    });
    if (sourceLength && currentTimeProp === totalTime)
        currentClipIndex = sourceLength - 1;
    return currentClipIndex;
};

/**
 * タイムライン指定位置のクリップ内の再生時間（ミリ秒）を取得
 * @param timelineCurrentTime タイムラインの現在再生時間（ミリ秒）
 * @param clipTimes クリップのそれぞれの再生時間（ミリ秒）を格納する配列
 * @returns タイムライン指定位置のクリップ内の再生時間（ミリ秒）
 */
export const GetClipCurrentTime = (
    timelineCurrentTime: number,
    clipTimes: number[]
): number => {
    if (!clipTimes || clipTimes.length === 0 || !timelineCurrentTime) return 0;

    const sourceIndex = GetCurrentClipIndexFromTime(timelineCurrentTime, clipTimes);
    let clipCurrentTime = 0;
    let clipPlayedTotalTime = 0;
    clipTimes.forEach((clipTime, index) => {
        if (sourceIndex > index) {
            clipPlayedTotalTime += clipTime;
        }
    });

    clipCurrentTime = timelineCurrentTime - clipPlayedTotalTime;
    return Math.round(clipCurrentTime / 1000) * 1000;
};

/**
 * 指定位置の再生が出来るテンプレートURLを取得
 * @param id テンプレートIDを渡す
 * @returns 指定位置の再生が出来るテンプレートURL
 */
export const GetTemplateUrlFromId = (id: number) => {
    const token = localStorage.getItem("cineonhub_access_token");
    const sourceUrl =
        process.env.REACT_APP_API_ENDPOINT + "/api/templateproxy/" + id + "?Authorization=" + token;
    return sourceUrl;
};
